import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { IDate, Table } from '@shared/widgets';
import { useGetAccountsStore, useGetAllTransactionsStore } from '@lib/store';
import { useParams } from 'react-router-dom';
import { AmountUtils } from '@shared/utils';
import { ColumnsType } from '@pages/payments/widgets';
import { GetAllTransactionsResponse } from '@shared/api/rest/accounts';

interface OptionsType {
  leftLabel: string;
  value: string;
}

interface TransactionsProps {
  selectedOptions: OptionsType;
  dateFilter?: IDate;
}

export const Transactions = (props: TransactionsProps) => {
  const params = useParams();
  const allTransactionsStore = useGetAllTransactionsStore();
  const accountsStore = useGetAccountsStore();

  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [sortedData, setSortedData] = useState<GetAllTransactionsResponse[]>(allTransactionsStore.data);

  useEffect(() => {
    allTransactionsStore.get(params.id!);
  }, [params]);

  const currentAccount = accountsStore.data.find((item) => item.Id === params.id);

  const filteredData = useMemo(() => {
    let data = allTransactionsStore.data;

    if (props.selectedOptions?.value === 'PaidIn') {
      data = data?.filter((row) => row.PaidIn! > 0);
    } else if (props.selectedOptions?.value === 'PaidOut') {
      data = data?.filter((row) => row.PaidOut! > 0);
    }

    if (props.dateFilter?.from || props.dateFilter?.to) {
      data = data?.filter((row) => {
        const transactionDate = dayjs(row.Date);
        const isAfterFrom = props.dateFilter?.from ? transactionDate.isAfter(dayjs(props.dateFilter.from), 'day') : true;
        const isBeforeTo = props.dateFilter?.to ? transactionDate.isBefore(dayjs(props.dateFilter.to), 'day') : true;
        return isAfterFrom && isBeforeTo;
      });
    }

    return data;
  }, [props.selectedOptions, props.dateFilter, allTransactionsStore.data]);

  useEffect(() => {
    const sorted = [...filteredData];
    if (sortOrder === 'asc') {
      sorted.sort((a, b) => (dayjs(a.Date).isBefore(dayjs(b.Date)) ? -1 : 1));
    } else if (sortOrder === 'desc') {
      sorted.sort((a, b) => (dayjs(a.Date).isBefore(dayjs(b.Date)) ? 1 : -1));
    }
    setSortedData(sorted);
  }, [sortOrder, filteredData]);

  const handleSort = () => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const columns: ColumnsType[] = [
    {
      title: 'Date',
      onSort: handleSort,
    },
    { title: 'DESCRIPTION' },
    { title: 'TO/FROM' },
    { title: 'PAID IN' },
    { title: 'PAID OUT' },
    { title: 'BALANCE' },
  ];

  return (
    <Table
      columns={columns}
      items={sortedData.map((row) => {
        return [
          {
            value: row.Date ? dayjs(row.Date).format('DD.MM.YYYY') : '--',
          },
          {
            value: row.Description ? row.Description : '--',
          },
          {
            value: row.FromTo ? row.FromTo : '--',
          },
          {
            value: row.PaidIn ? `${currentAccount?.Currency || ''} ${AmountUtils.transformToAmount(row.PaidIn)}` : '--',
          },
          {
            value: row.PaidOut ? `${currentAccount?.Currency || ''} ${AmountUtils.transformToAmount(row.PaidOut!)}` : '--',
          },
          {
            value: row.Balance ? `${currentAccount?.Currency || ''} ${AmountUtils.transformToAmount(row.Balance!)}` : '--',
          },
        ];
      })}
      loading={allTransactionsStore.loading}
      isScrollable
      sortOrder={sortOrder}
      accountTab
    />
  );
};
